import React, { useState, useEffect, useRef } from 'react';
import "./SingleSelect.css";
import BodySmallRegular from './BodySmallRegular';
import { Search, ChevronDown } from "lucide-react";
import { CircleAlert } from "lucide-react";
import { formConfigs } from '../addNew/formConfigs';
import { t } from '../../locales/translate';
const SingleSelect = ({
    options,
    value,
    onChange,
    disabled = false,
    placeholder = 'Select',
    tooltipExample,
    tooltipFormat,
    labelIconRight,
    label,
    isSearchable,
    secondaryLabel,
    type,
    icon,
    iconStart,
    iconEnd,
    error,
    labelIcon,
    maxHeight,
    showToggle = false,
    toggleLabel = "All Subordinates",
    alternateOptions = [],
    onAddNewClick,
    isLoading = false,
    onToggleChange = () => { },
    configKey,
    showClear = false,
}) => {
    const [selectedOption, setSelectedOption] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const searchInputRef = useRef(null);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [showTooltip, setShowTooltip] = useState(false);
    const [isToggleOn, setIsToggleOn] = useState(false);
    const [currentOptions, setCurrentOptions] = useState(options);

    // Subtext functionality
    const [selectedSubtext, setSelectedSubtext] = useState("");
    const [expandedSubtexts, setExpandedSubtexts] = useState({});

    const config = configKey ? formConfigs[configKey] || {} : null;
const permissions = JSON.parse(localStorage.getItem("permissionsList")) || [];
const canAdd =
  !!configKey && (!config?.requiredPermission || permissions.includes(config.requiredPermission));


    const toggleSubtextExpand = (e, optionValue) => {
        e.stopPropagation();
        setExpandedSubtexts(prev => ({
            ...prev,
            [optionValue]: !prev[optionValue]
        }));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && inputRef.current.contains(event.target)) {
                setIsOpen((prev) => !prev);
            } else if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            }
            if (isSearchable && searchInputRef.current) {
            }

            const preventSelection = (e) => {
                if (searchInputRef.current && !searchInputRef.current.contains(e.target) &&
                    dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                    e.preventDefault();
                }
            };

            document.addEventListener('selectstart', preventSelection);
            return () => document.removeEventListener('selectstart', preventSelection);
        }

        if (!isOpen) {
            setSearchQuery('');
        }
    }, [isOpen, isSearchable]);

    // Set current options based on toggle state
    useEffect(() => {
        setCurrentOptions(!isToggleOn && alternateOptions?.length > 0 ? alternateOptions : options);
    }, [isToggleOn, options, alternateOptions]);

    // Filter options based on search query
    useEffect(() => {
        if (searchQuery.trim()) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            setFilteredOptions(
                currentOptions?.filter((option) =>
                    option.label.toLowerCase().includes(lowerCaseQuery) ||
                    (option.subtext && option.subtext.toLowerCase().includes(lowerCaseQuery))
                )
            );
        } else {
            setFilteredOptions(currentOptions);
        }
    }, [searchQuery, currentOptions]);

    useEffect(() => {
        if (!value) {
            setSelectedOption('');
            setSelectedSubtext('');
            return;
        }
    
        const findInArray = (arr) => Array.isArray(arr) ? arr.find(option => option.value == value) : undefined;
    
        let matchedOption = findInArray(currentOptions);
    
        if (!matchedOption && !isToggleOn) {
            matchedOption = findInArray(options);
        }
    
        if (!matchedOption && isToggleOn) {
            matchedOption = findInArray(alternateOptions);
        }
    
        if (matchedOption) {
            setSelectedOption(matchedOption.label);
            setSelectedSubtext(matchedOption.subtext || "");
        } else {
            setSelectedOption('');
            setSelectedSubtext('');
        }
    }, [value, options, alternateOptions, currentOptions, isToggleOn]);
    const handleOptionClick = (option) => {
        setSelectedOption(option.label);
        setSelectedSubtext(option.subtext || "");
        onChange(option, label);
        setIsOpen(false);
    };

    const handleSearchInput = (e) => {
        e.stopPropagation();
        setSearchQuery(e.target.value);
    };

    const handleToggle = (e) => {
        e.stopPropagation();
        const newToggleState = !isToggleOn;
        setIsToggleOn(newToggleState);
        onToggleChange(newToggleState, value);
    };
        const handleClear = (e) => {
        e.stopPropagation();
        setSelectedOption('');
        setSelectedSubtext('');
        setSearchQuery('');
        onChange({ value: null, label: '' }, label);
        setIsOpen(false);
    };

    return (
        <div className={`custom-single-select-container ${disabled ? 'custom-disabled' : ''}`}>
            <div className='single-select-main'>
                <div className='custom-single-select-label-container'>
                    {labelIcon && labelIcon}
                    {label && label.length !== 0 && (
                        <div className="label-with-icons">
                            {label && <p className="label-text">{label}</p>                        }
                            {labelIconRight && (
                                <div
                                    className="icon-with-tooltip"
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                >
                                    {labelIconRight}
                                    {showTooltip && (tooltipFormat || tooltipExample) && (
                                        <div className="tooltip-box">
                                            {tooltipFormat && (
                                                <div className="tooltip-row">
                                                    <span className="tooltip-heading">Format:</span>
                                                    <span className="tooltip-text">{tooltipFormat}</span>
                                                </div>
                                            )}
                                            {tooltipExample && (
                                                <div className="tooltip-row">
                                                    <span className="tooltip-heading">Example:</span>
                                                    <span className="tooltip-text">{tooltipExample}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className='custom-single-select-div'>
                    <div className="custom-single-select" ref={inputRef}>
                        <span className='main-label-container'>{selectedOption || placeholder}</span>
                        <span className="custom-single-select-icon">
                            {icon ? icon : <ChevronDown />}
                        </span>
                    </div>
                    {typeof error === "string" && error.trim().length > 0 && (
                        <div className="error-single-select-container">
                            <CircleAlert height={16} />
                            <span className="single-select-error-text">
                                {error && <p>{error}</p>}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {isOpen && (
    <div className="custom-single-select-dropmenu-container" ref={dropdownRef}>
        {showToggle && (
            <div className="toggle-row">
                <span className="toggle-label">{toggleLabel}</span>
                <div
                    className={`toggle-switch ${isToggleOn ? 'toggle-on' : 'toggle-off'}`}
                    onClick={handleToggle}
                >
                    <div className="toggle-button"></div>
                </div>
            </div>
        )}

        {isLoading ? (
            <div className="custom-single-select-option">
                <span>Loading...</span>
            </div>
        ) : currentOptions && currentOptions.length > 0 ? (
            <>
                           {(isSearchable || showClear) && (
                                <div className='search-and-clear-wrapper'>
                                    {isSearchable && (
                                        <div className='custom-single-select-search-container'>
                                    <input
                                        ref={searchInputRef}
                                        className='search-input'
                                        placeholder='Search'
                                        value={searchQuery}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                        }}
                                        onKeyDown={(e) => {
                                            e.stopPropagation();
                                        }}
                                        onFocus={(e) => {
                                            e.stopPropagation();
                                            if (document.getSelection) {
                                                document.getSelection().empty();
                                            }
                                        }}
                                        onChange={handleSearchInput}
                                    />
                                     <Search width={18} />
                                    </div>
                                    )}
                                    {showClear && (
                                        <button
                                            type="button"
                                            className="clear-all-button"
                                            onClick={handleClear}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            )}
                            <div
                                className="custom-single-select-options"
                                style={maxHeight ? { maxHeight: maxHeight } : {}}
                            >
                                {filteredOptions && filteredOptions.length > 0 ? filteredOptions.map((option) => (
                                    option.value === 'divider' ? (
                                        <div key={option.value} className="dropdown-divider"></div>
                                    ) : (
                                        <div
                                            key={option.value}
                                            className="custom-single-select-option"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOptionClick(option);
                                            }}
                                        >
                                            <div className='primary-label-div'>
                                                {type && type === "radio" && (
                                                    <input
                                                        type="radio"
                                                        className="custom-radio-input"
                                                        checked={value === option.value}
                                                        readOnly
                                                    />
                                                )}
                                                {iconStart && iconStart}
                                                <div className="option-text-container">
                                                    <span className='label-text'>{option.label}</span>
                                                    {option.subtext && (
                                                        <span
                                                            className={`option-subtext ${expandedSubtexts[option.value] ? 'expanded' : ''}`}
                                                            title={option.subtext}
                                                            onClick={(e) => toggleSubtextExpand(e, option.value)}
                                                        >
                                                            {option.subtext}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {secondaryLabel && option.secondaryLabel && (
                                                <span className='secondary-label'>{option.secondaryLabel}</span>
                                            )}
                                            {iconEnd && iconEnd}
                                        </div>
                                    )
                                )) : (
                                    <div className="custom-single-select-option">
                                        <span>No data</span>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="custom-single-select-option">
                            <span>No data</span>
                        </div>
                    )}
                   {onAddNewClick && canAdd && (
                    <div className="custom-single-select-add-new-container">
                        <button
                        type="button"
                        className="custom-single-select-add-new-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddNewClick();
                        }}
                        >
                        {`+ ${t("addNew")}`}
                        </button>
                    </div>
                            )}

                </div>
            )}
        </div>
    );
};

export default SingleSelect;